/**
 * Customer intent tracking.
 *
 * Records which "occasion" collections a customer browsed (sympathy, birthday,
 * etc.) so product pages can offer add-ons that match why they are buying —
 * but only when the product actually belongs to that occasion.
 *
 * Because product URLs are not collection-contextual, the collection page
 * snapshots its fully resolved add-on tabs (handle, title, image) into storage.
 * A short visit history lets products in multiple occasion collections pick the
 * most recently browsed one.
 */
(function () {
  const STORAGE_KEY = 'ffe:intent';
  const TTL_MS = 24 * 60 * 60 * 1000;
  const MAX_VISITS = 10;

  function clear() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* storage unavailable */
    }
  }

  function normalizeVisit(payload) {
    if (!payload || !payload.handle || !Array.isArray(payload.tabs)) return null;

    const tabs = payload.tabs
      .filter(function (tab) {
        return tab && tab.handle;
      })
      .map(function (tab) {
        return {
          handle: String(tab.handle),
          title: String(tab.title || tab.handle),
          upsell_title: tab.upsell_title ? String(tab.upsell_title) : '',
          image: tab.image ? String(tab.image) : '',
        };
      });

    if (!tabs.length) return null;

    return {
      handle: String(payload.handle),
      title: String(payload.title || payload.handle),
      tabs: tabs,
      savedAt: typeof payload.savedAt === 'number' ? payload.savedAt : Date.now(),
    };
  }

  function readVisits() {
    let raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return [];
    }

    if (!raw) return [];

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      clear();
      return [];
    }

    // Migrate the previous single-record shape: { handle, tabs, savedAt }
    var list = Array.isArray(parsed)
      ? parsed
      : parsed && parsed.handle
        ? [parsed]
        : [];

    var now = Date.now();
    var visits = list
      .map(normalizeVisit)
      .filter(function (visit) {
        return visit && now - visit.savedAt <= TTL_MS;
      });

    if (!visits.length && list.length) clear();
    return visits;
  }

  function writeVisits(visits) {
    try {
      if (!visits.length) {
        clear();
        return;
      }
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(visits));
    } catch (error) {
      /* storage unavailable, intent is best-effort */
    }
  }

  function save(payload) {
    var visit = normalizeVisit(payload);
    if (!visit) return null;

    visit.savedAt = Date.now();

    var visits = readVisits().filter(function (existing) {
      return existing.handle !== visit.handle;
    });
    visits.unshift(visit);
    writeVisits(visits.slice(0, MAX_VISITS));

    return visit;
  }

  // Most recent visit overall (kept for debugging / simple reads).
  function get() {
    var visits = readVisits();
    return visits.length ? visits[0] : null;
  }

  // Most recent visit whose handle is in the given collection list.
  // Used so a sympathy browse does not colour a birthday-only product.
  function getForCollections(handles) {
    if (!handles || !handles.length) return null;

    var allowed = {};
    handles.forEach(function (handle) {
      if (handle) allowed[String(handle)] = true;
    });

    var visits = readVisits();
    for (var i = 0; i < visits.length; i++) {
      if (allowed[visits[i].handle]) return visits[i];
    }
    return null;
  }

  window.FFEIntent = {
    get: get,
    getForCollections: getForCollections,
    save: save,
    clear: clear,
    STORAGE_KEY: STORAGE_KEY,
    TTL_MS: TTL_MS,
  };

  // The tracker snippet renders inline during parse, before this deferred
  // script runs, so it hands the payload over rather than calling save directly.
  if (window.FFEIntentPending) {
    save(window.FFEIntentPending);
  }
})();
